import { useMemo, useEffect, useState } from 'react';
import { getRoomTypes } from '../api/itrsApi';
import { useWatch } from 'react-hook-form';
import useMuiDndEditorTable from '../../shared/useMuiDndEditorTable';

const BASE_COLS = [
  { name: 'Id', field: 'id', width: 0, hidden: true },
  {
    name: 'Room Type',
    field: 'roomType',
    width: 40,
    inputType: 'dropdown',
    type: 'dropdown',
    getOptions: getRoomTypes,
    options: [],
  },
  {
    name: 'Start Date/Time',
    field: 'startDate',
    width: 20,
    inputType: 'datetime-local',
    textFieldProps: { inputProps: { placeholder: 'Start Date/Time' } },
  },
  {
    name: 'End Date/Time',
    field: 'endDate',
    width: 20,
    inputType: 'datetime-local',
    textFieldProps: { inputProps: { placeholder: 'End Date/Time' } },
  },
  {
    name: '',
    width: 5,
    noSort: true,
    isRemove: true,
    confirm: { title: 'Remove Room', message: 'Are you sure you want to remove this room?' },
  },
];

const toLocalInput = (d, endOfDay = false) => {
  if (!d) return undefined;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return `${d}T${endOfDay ? '23:59' : '00:00'}`;
  }
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date)) return undefined;
  if (endOfDay) date.setHours(23, 59, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

// Helper function to compare datetime strings
const compareDateTime = (datetime1, datetime2) => {
  if (!datetime1 || !datetime2) return 0;
  return new Date(datetime1) - new Date(datetime2);
};

const useRoomColumns = (form, sessionStart, sessionEnd) => {
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const roomReservations = useWatch({
    control: form.control,
    name: 'roomReservations',
    defaultValue: [], // avoid undefined on first render
  });

  useEffect(() => {
    let foundAnyError = false;

    roomReservations.forEach((row, idx) => {
      const startField = `roomReservations.${idx}.startDate`;
      const endField = `roomReservations.${idx}.endDate`;

      if (!row.startDate || !row.endDate) {
        foundAnyError = true;

        if (row.startDate) {
          form.clearErrors(startField);
        } else {
          form.setError(startField, {
            type: 'manual',
            message: 'Start date is required',
          });
        }

        if (row.endDate) {
          form.clearErrors(endField);
        } else {
          form.setError(endField, {
            type: 'manual',
            message: 'End date is required',
          });
        }
        return;
      }

      form.clearErrors(startField);
      form.clearErrors(endField);

      const onlyDate = (iso) => iso?.split('T')[0];
      const startOnly = onlyDate(row.startDate);
      const endOnly = onlyDate(row.endDate);

      // Validate start date bounds
      let startMsg = null;
      if (row.startDate) {
        if (startOnly < sessionStart) startMsg = `Cannot be before ${sessionStart}`;
        else if (startOnly > sessionEnd) startMsg = `Cannot be after ${sessionEnd}`;
      }
      if (startMsg) {
        form.setError(startField, { type: 'manual', message: startMsg });
        foundAnyError = true; // ADD: Track that we found an error
      } else {
        form.clearErrors(startField);
      }

      // Validate end date bounds
      let endMsg = null;
      if (row.endDate) {
        if (endOnly < sessionStart) endMsg = `Cannot be before ${sessionStart}`;
        else if (endOnly > sessionEnd) endMsg = `Cannot be after ${sessionEnd}`;
      }
      if (endMsg) {
        form.setError(endField, { type: 'manual', message: endMsg });
        foundAnyError = true; // ADD: Track that we found an error
      } else {
        form.clearErrors(endField);
      }

      // Enhanced date/time comparison validation
      if (row.startDate && row.endDate && !startMsg && !endMsg) {
        const dateTimeComparison = compareDateTime(row.startDate, row.endDate);

        if (dateTimeComparison > 0) {
          // Start datetime is after end datetime
          form.setError(startField, {
            type: 'manual',
            message: 'Start date/time cannot be after end date/time',
          });
          form.setError(endField, {
            type: 'manual',
            message: 'End date/time cannot be before start date/time',
          });
          foundAnyError = true; // ADD: Track that we found an error
        } else if (dateTimeComparison === 0) {
          // Start and end datetime are exactly the same
          form.setError(startField, {
            type: 'manual',
            message: 'Start and end date/time cannot be the same',
          });
          form.setError(endField, {
            type: 'manual',
            message: 'Start and end date/time cannot be the same',
          });
          foundAnyError = true; // ADD: Track that we found an error
        } else {
          // Valid: start is before end - clear any leftover cross-field errors
          form.clearErrors(startField);
          form.clearErrors(endField);
        }
      }
    });

    setHasValidationErrors(foundAnyError);
    form.trigger();
  }, [roomReservations, sessionStart, sessionEnd, form]);

  const minIso = toLocalInput(sessionStart);
  const maxIso = toLocalInput(sessionEnd, true);

  const columns = useMemo(() => {
    return BASE_COLS.map((col) => {
      if (col.field === 'startDate' || col.field === 'endDate') {
        return {
          ...col,
          textFieldProps: {
            ...col.textFieldProps,
            InputLabelProps: { shrink: true },
            inputProps: {
              ...col.textFieldProps.inputProps,
              min: minIso,
              max: maxIso,
            },
          },
        };
      }
      return col;
    });
  }, [sessionStart, sessionEnd]);

  const { addRow, PreRenderedTable } = useMuiDndEditorTable({
    form,
    columns,
    formRowsKey: 'roomReservations',
    defaultRow: { roomType: 'classroom' },
    canDrag: false,
    fontSize: '1rem',
  });

  return { addRow, PreRenderedTable, hasValidationErrors };
};

export default useRoomColumns;
